/**
 * Per-entity-type capabilities for shareable links.
 *
 * canBeSharedViaLink  — false hides the copy-link button in EntityPermissionsModal
 * canBePublic         — false hides the "Anyone with link" toggle (maps to subject:'public' in ACL)
 * standaloneAlias     — nicer URL alias for this entity type (e.g. /automations/:id → /entity/:id semantically)
 * standaloneBadge     — OG preview image badge label; defaults to entityType last segment
 *
 * IMPORTANT: Any type NOT listed here gets DEFAULT_CAPABILITY.
 * When in doubt, default canBePublic: false — flipping it on is trivial; a leaked toggle is a bug.
 *
 * NOTE: The Python equivalent lives in backend/services/entity_link_service.py.
 * If you add or change an entry here, mirror it there and the parity test will catch drift.
 */

export interface EntityLinkCapability {
  canBeSharedViaLink: boolean;
  canBePublic: boolean;
  standaloneAlias?: string;
  standaloneBadge?: string;
}

/** Returned for any entity type not in ENTITY_LINK_CAPABILITIES. */
export const DEFAULT_CAPABILITY: EntityLinkCapability = {
  canBeSharedViaLink: true,
  canBePublic: false,
};

/** Explicit capabilities for known entity types. */
export const ENTITY_LINK_CAPABILITIES: Record<string, EntityLinkCapability> = {
  // ---- Content types (publicly shareable) ----
  Doc: {
    canBeSharedViaLink: true,
    canBePublic: true,
    standaloneAlias: '/docs',
    standaloneBadge: 'Document',
  },
  Note: {
    canBeSharedViaLink: true,
    canBePublic: true,
    standaloneBadge: 'Note',
  },
  Canvas: {
    canBeSharedViaLink: true,
    canBePublic: true,
    standaloneBadge: 'Canvas',
  },

  // ---- Automation / Agent types (publicly shareable) ----
  Automation: {
    canBeSharedViaLink: true,
    canBePublic: true,
    standaloneAlias: '/automations',
    standaloneBadge: 'Automation',
  },
  Agent: {
    canBeSharedViaLink: true,
    canBePublic: true,
    standaloneAlias: '/automations',
    standaloneBadge: 'Agent',
  },
  OrchestratorAgent: {
    canBeSharedViaLink: true,
    canBePublic: true,
    standaloneAlias: '/automations',
    standaloneBadge: 'Agent',
  },

  // ---- Spreadsheet types ----
  Sheet: {
    canBeSharedViaLink: true,
    canBePublic: false,
    standaloneAlias: '/sheets',
    standaloneBadge: 'Spreadsheet',
  },
  Spreadsheet: {
    canBeSharedViaLink: true,
    canBePublic: false,
    standaloneAlias: '/sheets',
    standaloneBadge: 'Spreadsheet',
  },

  // ---- Organisation structures (shareable, but not publicly) ----
  Space: {
    canBeSharedViaLink: true,
    canBePublic: false,
    standaloneBadge: 'Space',
  },
  Group: {
    canBeSharedViaLink: true,
    canBePublic: false,
    standaloneBadge: 'Folder',
  },

  // ---- Credentials & integrations — never shareable ----
  Credential: {
    canBeSharedViaLink: false,
    canBePublic: false,
  },
  Integration: {
    canBeSharedViaLink: false,
    canBePublic: false,
  },
  APIConnection: {
    canBeSharedViaLink: false,
    canBePublic: false,
  },
  MCPConnection: {
    canBeSharedViaLink: false,
    canBePublic: false,
  },
};

/**
 * Get the capability for a given entityType.
 * Supports prefix matching: 'Integration.Gmail' resolves to 'Integration'.
 */
export function getCapability(entityType: string): EntityLinkCapability {
  if (!entityType) return DEFAULT_CAPABILITY;

  // Exact match first
  if (ENTITY_LINK_CAPABILITIES[entityType]) {
    return ENTITY_LINK_CAPABILITIES[entityType];
  }

  // Walk up the dot-notation hierarchy (e.g. Integration.Gmail → Integration)
  const parts = entityType.split('.');
  for (let i = parts.length - 1; i > 0; i--) {
    const key = parts.slice(0, i).join('.');
    if (ENTITY_LINK_CAPABILITIES[key]) {
      return ENTITY_LINK_CAPABILITIES[key];
    }
  }

  return DEFAULT_CAPABILITY;
}
